import { ChangeEvent, useContext } from "react";
import { isEmpty } from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { MIME_TYPE } from "common/constants";
import { getPath } from "common/routing";
import { formatDate } from "common/utils/strings";
import { useDispatchBulkDocumentGenerationScriptJobMutation } from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { AuthContext } from "../../../Authorization/AuthContext";
import {
  Container,
  FormContainer,
  Section,
} from "../../../Common/__styles__/FullPageFormLayout";
import FullPageFormLayout from "../../../Common/FullPageFormLayout";
import { Body, Title } from "../../../Common/Typography";
import { Checkbox, Datepicker } from "../../../Inputs/react-hook-form";
import { FormTitleWrapper } from "./__styles__/Operations";
import {
  AccountCustomMapSelect,
  AccountDocumentTemplateSelect,
  AccountPropertyWarningDefinitionSelect,
  BulkDocumentGenerationForm,
  InputFile,
  useDispatchBulkDocumentGenerationFileUpload,
} from "./shared";

type DispatchBulkDocumentGenerationForm = BulkDocumentGenerationForm & {
  templateId: string;
  accountPropertyWarningDefinitionIds?: string[];
  hiddenFromPublic?: boolean;
  ignorePropertiesWithDocumentCreatedAfter?: string;
  todaysDate?: string;
  nonMitigatedRepLossProperties?: boolean;
  repLossProperties?: boolean;
  customMapId?: string;
};

export const DispatchBulkDocumentGeneration = () => {
  const formMethods = useForm<DispatchBulkDocumentGenerationForm>({
    defaultValues: {
      hiddenFromPublic: true,
    },
  });

  const history = useHistory();
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const { admin } = useContext(AuthContext);
  const canViewPII = admin?.canViewPersonalIdentifiableInformation ?? false;

  const { uploadFiles, isUploading } =
    useDispatchBulkDocumentGenerationFileUpload<DispatchBulkDocumentGenerationForm>(
      formMethods,
    );

  const [dispatch, { loading: isDispatching }] =
    useDispatchBulkDocumentGenerationScriptJobMutation({
      onCompleted: () => {
        addSuccessToast("Dispatched bulk document generation operation!");
        history.push(getPath("operations"));
      },
      onError: () => {
        addErrorToast("Failed to dispatch bulk document generation operation.");
      },
    });

  const { handleSubmit, watch, control, setValue } = formMethods;

  const templateIdValue = watch("templateId");
  const repLossPropertiesValue = watch("repLossProperties");
  const nonMitigatedRepLossPropertiesValue = watch(
    "nonMitigatedRepLossProperties",
  );
  const isRepLossSelected =
    repLossPropertiesValue || nonMitigatedRepLossPropertiesValue;

  const onRepLossChange = (evt: ChangeEvent<HTMLInputElement>) => {
    if (evt.target.checked) {
      setValue("hiddenFromPublic", true);
    }
  };

  const missingRequiredFields = isEmpty(templateIdValue);

  const onSubmit = async (formData: DispatchBulkDocumentGenerationForm) => {
    const uploadResult = await uploadFiles();

    if (!uploadResult) {
      return;
    }

    const formattedTodaysDate =
      formData.todaysDate ? formatDate(formData.todaysDate) : undefined;

    await dispatch({
      variables: {
        data: {
          templateId: formData.templateId,
          accountPropertyWarningDefinitionIds:
            formData.accountPropertyWarningDefinitionIds,
          propertiesFile: uploadResult.propertiesFileKey,
          overridesFile: uploadResult.overridesFileKey,
          labelMappingsFile: uploadResult.labelMappingsFileKey,
          customMapId: formData.customMapId,
          nonMitigatedRepLossProperties:
            formData.nonMitigatedRepLossProperties ?? false,
          repLossProperties: formData.repLossProperties ?? false,
          hiddenFromPublic: formData.hiddenFromPublic,
          ignorePropertiesWithDocumentCreatedAfter:
            formData.ignorePropertiesWithDocumentCreatedAfter,
          todaysDate: formattedTodaysDate,
        },
      },
    });
  };

  return (
    <FullPageFormLayout
      subtitle="Bulk document generation actions"
      prevLocation={getPath("operations")}
      buttonLoading={isUploading || isDispatching}
      onSave={handleSubmit(onSubmit)}
      buttonDisabled={missingRequiredFields}
    >
      <Container>
        <FormContainer>
          <FormProvider {...formMethods}>
            <form>
              <FormTitleWrapper>
                <Title size="large" type="semiBold">
                  Bulk document generation actions
                </Title>
              </FormTitleWrapper>
              <Body size={"default"} type={"regular"} color="contentSecondary">
                This form will allow you to generate documents from templates
                for multiple properties at once.
                <br />
                <br />
                When the script is complete you will receive an email with a
                link to download a CSV containing the results and any errors
                that occurred during the operation.
              </Body>

              <FormTitleWrapper>
                <Title size={"small"} type={"regular"}>
                  Required settings
                </Title>
              </FormTitleWrapper>
              <Section>
                <AccountDocumentTemplateSelect
                  required={true}
                  description="Select the document template to use for generation"
                />
                <Body
                  size={"default"}
                  type={"regular"}
                  color="contentSecondary"
                >
                  Use only one of the following fields to determine how to
                  select the properties to generate documents for:
                  <br />
                  &nbsp;&nbsp;- Properties file
                  <br />
                  &nbsp;&nbsp;- Property warning definitions
                  <br />
                  &nbsp;&nbsp;- Custom map
                  {canViewPII && (
                    <>
                      <br />
                      &nbsp;&nbsp;- Non-mitigated rep loss properties
                      <br />
                      &nbsp;&nbsp;- Rep loss properties
                    </>
                  )}
                  <br />
                </Body>
                <InputFile
                  name="propertiesFile"
                  label="Properties file"
                  description="Provide a CSV file containing property information to generate documents for. Each line in the file should contain at least a property id or a full property address. Optionally, a parcel number can be provided."
                  allowedMimeTypes={[MIME_TYPE.CSV]}
                />
                <AccountPropertyWarningDefinitionSelect description="Select the warning definitions that must be present on a property for it to get a document generated. Note this list is being used as an `or`, so a property with any of these warnings will be included." />
                <AccountCustomMapSelect description="If selected, the properties to generate documents for will be filtered down to only those that intersect with the provided custom map." />
                {canViewPII && (
                  <>
                    <Checkbox
                      control={control}
                      label="Non-mitigated rep loss properties"
                      description="If true, the properties to generate documents for will be filtered down to only those that have non-mitigated repetitive loss entries."
                      name="nonMitigatedRepLossProperties"
                      additionalOnChange={onRepLossChange}
                    />
                    <Checkbox
                      control={control}
                      label="Rep loss properties"
                      description="If true, the properties to generate documents for will be filtered down to only those that have repetitive loss entries (regardless of mitigation status)."
                      name="repLossProperties"
                      additionalOnChange={onRepLossChange}
                    />
                  </>
                )}
              </Section>

              <FormTitleWrapper>
                <Title size={"small"} type={"regular"}>
                  Optional settings
                </Title>
              </FormTitleWrapper>
              <Section>
                <InputFile
                  name="overridesFile"
                  label="Data overrides file"
                  description="Provide a JSON file containing data override mappings for document generation. The JSON should map dot notation paths to CSV column headers (e.g., {'parcel.owner': 'ownerName'})."
                  allowedMimeTypes={[MIME_TYPE.JSON]}
                />
                <InputFile
                  name="labelMappingsFile"
                  label="Label mappings file"
                  description="Provide a JSON file that maps PDF field labels to override column names. This allows overriding fields that aren't mapped to Forerunner datapoints (e.g., {'Damage Event': 'damageEvent'} tells us to use the 'damageEvent' column from the overrides file for the 'Damage Event' PDF field)."
                  allowedMimeTypes={[MIME_TYPE.JSON]}
                />
                <Datepicker
                  control={control}
                  label="Ignore properties with document created after specific date"
                  description="If set, documents will not be generated for properties that already have a document of the same document type created after the set date."
                  name="ignorePropertiesWithDocumentCreatedAfter"
                />
                <Datepicker
                  control={control}
                  label="Override document date"
                  description="If set, the date in the document (normally today's date) will be hard coded to the set date"
                  name="todaysDate"
                />
                <Checkbox
                  control={control}
                  label="Hidden from public"
                  description={
                    isRepLossSelected ?
                      "Documents generated for rep loss properties are always hidden from public."
                    : "If checked, the generated documents will not be visible to the public. If unchecked, documents will be shown to the public."
                  }
                  name="hiddenFromPublic"
                  disabled={isRepLossSelected}
                />
              </Section>
            </form>
          </FormProvider>
        </FormContainer>
      </Container>
    </FullPageFormLayout>
  );
};
